import { curSym } from "@/lib/company";
import { DatePickerTR } from "@/components/DatePickerTR";
import { useState, useEffect } from "react";
import api, { apiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { BANKS, BankBadge } from "@/lib/banks";

// Kullanıcı ekranından hızlı personel kaydı (zorunlu alanlar + banka).
export default function NewStaffDialog({ open, onClose, onCreated }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const empty = { name: "", national_id: "", phone: "", email: "", hire_date: "", bank: "", title: "", monthly_salary: "", default_role: "cashier" };
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [roles, setRoles] = useState([]);
  useEffect(() => { if (open) api.get("/roles").then((r) => setRoles(r.data || [])).catch(() => {}); }, [open]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.name.trim()) return toast.error("Ad Soyad zorunlu");
    if (!isAdmin) {
      if (form.national_id.trim().length !== 11) return toast.error("TC Kimlik No 11 haneli olmalı");
      if (!form.phone.trim()) return toast.error("Telefon zorunlu");
      if (!form.email.trim() || !form.email.includes("@")) return toast.error("Geçerli bir e-posta zorunlu");
      if (!form.hire_date) return toast.error("İşe giriş tarihi zorunlu");
      if (!form.bank) return toast.error("Banka seçimi zorunlu");
      if (!form.title.trim()) return toast.error("Görev/pozisyon zorunlu");
    }
    setBusy(true);
    try {
      const { data } = await api.post("/users/staff", {
        name: form.name.trim(), national_id: form.national_id.trim(), phone: form.phone.trim(),
        email: form.email.trim(), hire_date: form.hire_date, bank: form.bank,
        title: form.title.trim() || null,
        default_role: form.default_role || null,
        monthly_salary: parseFloat(String(form.monthly_salary).replace(",", ".")) || 0,
        account: "bank", active: true,
      });
      toast.success("Personel kaydı oluşturuldu");
      onCreated(data);
      setForm(empty);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="new-staff-dialog" className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-heading">Yeni Personel</DialogTitle>
          <DialogDescription>Kullanıcı bir personel kaydına bağlanır. Zorunlu alanları doldurun; kayıt Personel dizinine eklenir.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div><Label>Ad Soyad *</Label><Input data-testid="new-staff-name" value={form.name} onChange={(e) => set("name", e.target.value)} className="mt-1" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>TC Kimlik No *</Label><Input data-testid="new-staff-tckn" inputMode="numeric" maxLength={11} value={form.national_id} onChange={(e) => set("national_id", e.target.value.replace(/\D/g, ""))} className="mt-1 tabular" placeholder="11 hane" /></div>
            <div><Label>Telefon *</Label><Input data-testid="new-staff-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} className="mt-1" placeholder="05xx xxx xx xx" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>E-posta *</Label><Input data-testid="new-staff-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="mt-1" placeholder="ornek@eposta.com" /></div>
            <div><Label>İşe Giriş Tarihi *</Label><DatePickerTR testid="new-staff-hire" value={form.hire_date} onChange={(v) => set("hire_date", v)} /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Görev / Pozisyon *</Label><Input data-testid="new-staff-title" value={form.title} onChange={(e) => set("title", e.target.value)} className="mt-1" placeholder="Kasiyer, Depo vb." /></div>
            <div><Label>Aylık Maaş {curSym()}</Label><Input data-testid="new-staff-salary" inputMode="decimal" value={form.monthly_salary} onChange={(e) => set("monthly_salary", e.target.value)} className="mt-1 tabular" placeholder="0,00" /></div>
          </div>
          <div>
            <Label>Sistem Rolü (kullanıcı eşleştirmede otomatik atanır) *</Label>
            <select data-testid="new-staff-role" value={form.default_role} onChange={(e) => set("default_role", e.target.value)}
              className="mt-1 h-10 w-full rounded-md border border-input bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand-green">
              {roles.filter((r) => !r.disabled).map((r) => <option key={r.code} value={r.code}>{r.label}</option>)}
            </select>
            <p className="mt-1 text-[11px] text-slate-400">Bu personel bir kullanıcıya eşleştiğinde rolü buradan gelir ve değiştirilemez.</p>
          </div>
          <div>
            <Label>Banka *</Label>
            <select data-testid="new-staff-bank" value={form.bank} onChange={(e) => set("bank", e.target.value)}
              className="mt-1 h-10 w-full rounded-md border border-input bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand-green">
              <option value="">Banka seçin…</option>
              {BANKS.map((b) => <option key={b.code} value={b.code}>{b.name}</option>)}
            </select>
            {form.bank && <div className="mt-1.5"><BankBadge code={form.bank} /></div>}
          </div>
          <Button data-testid="new-staff-save" onClick={save} disabled={busy} className="w-full bg-brand-green text-white hover:bg-brand-green-600">
            {busy ? "Kaydediliyor..." : "Personeli Kaydet ve Eşleştir"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
